import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { medicalStatus } from "@/lib/medical";
import { useAuth } from "@/context/AuthContext";
import AttachmentsPanel from "@/components/AttachmentsPanel";
import PlayerRffmSection from "@/components/PlayerRffmSection";
import MinutesBreakdownDialog from "@/components/MinutesBreakdownDialog";
import { MIN_A, MIN_B } from "@/lib/minutesStats";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  ArrowLeft, Loader2, Phone, CalendarDays, Shirt, Trophy, Timer,
  Activity, HeartPulse, ClipboardCheck, Users, FileDown, Square, Paperclip,
} from "lucide-react";
import { exportPlayerDashboardPdf, exportPlayerEvaluationPdf } from "@/lib/export";
import {
  ResponsiveContainer, PieChart, Pie, Cell, Tooltip, Legend,
  BarChart, Bar, XAxis, YAxis, CartesianGrid, LineChart, Line,
  RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
} from "recharts";

const ATT_COLORS = { Asiste: "#16A34A", Retraso: "#CA8A04", "No asiste": "#DC2626", Enfermo: "#F59E0B", "Lesión": "#EA580C", Personal: "#64748B" };
const GRADE_STYLE = {
  Excelente: "bg-emerald-100 text-emerald-800",
  Notable: "bg-blue-100 text-blue-800",
  Bien: "bg-amber-100 text-amber-800",
  Mejorable: "bg-red-100 text-red-700",
  "—": "bg-slate-100 text-slate-500",
};
const TL_COLOR = { verde: "#16A34A", amarillo: "#F59E0B", rojo: "#DC2626" };
const STATUS_LABEL = { titular: "Titular", suplente: "Suplente", convocado: "Convocado", no_convocado: "No conv.", lesion: "Lesión", no_disponible: "No disp." };
const EVAL_PERIOD_LABEL = { octubre: "Octubre", mayo: "Mayo" };

const Kpi = ({ icon: Icon, label, value, sub, color = "text-purple-700", testId, onClick }) => (
  <Card
    className={`border-slate-200 ${onClick ? "cursor-pointer hover:border-purple-300 hover:shadow-sm transition-all" : ""}`}
    data-testid={testId}
    onClick={onClick}
    role={onClick ? "button" : undefined}
  >
    <CardContent className="p-4 flex items-center gap-3">
      <div className={`w-10 h-10 rounded-lg bg-slate-50 grid place-items-center shrink-0 ${color}`}>
        <Icon className="h-5 w-5" />
      </div>
      <div className="min-w-0">
        <div className="text-xl font-extrabold text-slate-900 leading-tight">{value}</div>
        <div className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide">{label}</div>
        {sub && <div className="text-[11px] text-slate-400">{sub}</div>}
      </div>
    </CardContent>
  </Card>
);

const Section = ({ title, icon: Icon, children, testId }) => (
  <Card className="border-slate-200" data-testid={testId}>
    <CardHeader className="pb-2">
      <CardTitle className="text-sm font-extrabold text-slate-800 flex items-center gap-2">
        {Icon && <Icon className="h-4 w-4 text-purple-600" />} {title}
      </CardTitle>
    </CardHeader>
    <CardContent>{children}</CardContent>
  </Card>
);

const Empty = ({ text }) => <p className="text-xs text-slate-400 py-6 text-center">{text}</p>;

export default function PlayerDashboard() {
  const { playerId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const backTeamId = searchParams.get("team_id") || "";
  const backSeason = searchParams.get("season") || "";
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [phase, setPhase] = useState(searchParams.get("phase") || "COMPETITION");
  const [minBreakdown, setMinBreakdown] = useState(false);
  const detailQs = `?scope=${encodeURIComponent(phase || "no_pre")}${backTeamId ? `&team_id=${backTeamId}` : ""}${backSeason ? `&season=${backSeason}` : ""}`;

  useEffect(() => {
    setLoading(true);
    api.get(`/players/${playerId}/dashboard`, { params: phase ? { phase } : {} })
      .then((r) => setData(r.data))
      .catch((err) => toast.error(formatApiError(err)))
      .finally(() => setLoading(false));
  }, [playerId, phase]);

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center py-24" data-testid="player-dashboard-loading">
        <Loader2 className="h-8 w-8 animate-spin text-purple-600" />
      </div>
    );
  }
  if (!data) return <Empty text="No se pudo cargar el jugador." />;

  const { player, team, stats, matches_detail, goals, cards, physical_tests, has_physical_data, load, wellness, evaluations, global_rating_areas } = data;
  const guest = data.guest || { matches: [], trainings: [], totals: {} };
  const gt = guest.totals || {};
  const hasGuest = (guest.matches?.length || 0) + (guest.trainings?.length || 0) > 0;

  const attData = [
    { name: "Asiste", value: stats.asiste },
    { name: "Retraso", value: stats.retraso || 0 },
    { name: "No asiste", value: stats.no_asiste },
    { name: "Enfermo", value: stats.enfermo },
    { name: "Lesión", value: stats.lesion_att },
    { name: "Personal", value: stats.motivos_personales || 0 },
  ].filter((d) => d.value > 0);

  const convData = [
    { name: "Titular", v: stats.titular },
    { name: "Suplente", v: stats.suplente },
    { name: "Convocado", v: stats.convocado },
    { name: "No conv.", v: stats.no_convocado },
    { name: "Lesión", v: stats.lesion },
  ];

  const wellnessSeries = [...(wellness.recent || [])].reverse().map((w) => ({
    date: (w.date || "").slice(5), score: w.score, tl: w.traffic_light,
  }));
  const rpeSeries = [...(load.recent || [])].reverse().map((r) => ({
    date: (r.date || "").slice(5), carga: r.internal_load || 0,
  }));

  const cr = data.comparative_radar;
  const shortArea = (l) => (l || "")
    .replace("Perfil EFC — Como ", "Perfil ")
    .replace("Impacto en el juego", "Impacto");
  const radarData = cr && cr.axes?.length
    ? cr.axes.map((label, i) => ({
        area: label,
        short: shortArea(label),
        Octubre: cr.octubre?.[i] || 0,
        Mayo: cr.mayo?.[i] || 0,
      }))
    : [];
  const hasRadar = radarData.some((d) => d.Octubre > 0 || d.Mayo > 0);
  const radarIsSections = cr?.source === "sections";
  const hasOct = radarData.some((d) => d.Octubre > 0);
  const hasMay = radarData.some((d) => d.Mayo > 0);

  const acwrBadge = load.acwr == null
    ? { txt: "Sin datos", cls: "bg-slate-100 text-slate-500" }
    : load.acwr > 1.5
      ? { txt: `ACWR ${load.acwr} · Riesgo alto`, cls: "bg-red-100 text-red-700" }
      : load.acwr < 0.8
        ? { txt: `ACWR ${load.acwr} · Baja carga`, cls: "bg-amber-100 text-amber-800" }
        : { txt: `ACWR ${load.acwr} · Óptimo`, cls: "bg-emerald-100 text-emerald-800" };

  // Los porcentajes de asistencia y minutos excluyen la pretemporada cuando se
  // muestra la temporada completa (en vistas por fase se usan los de esa fase).
  const comp = stats.sin_pretemporada || {};
  const isTotal = phase === "";
  const attPct = isTotal ? (comp.attendance_pct ?? 0) : stats.attendance_pct;
  const attSub = isTotal
    ? `${comp.asiste || 0}/${comp.total_trainings || 0} entrenos (sin pretemp.)`
    : `${stats.asiste}/${stats.total_trainings} entrenos`;
  const minPct = isTotal ? (comp.minutes_pct_called ?? 0) : stats.minutes_pct_called;
  const minPctSub = isTotal ? `${minPct}% convocado (sin pretemp.)` : `${minPct}% convocado`;
  // Dos indicadores diferenciados de minutos (A equipo / B convocado).
  const _pwr = isTotal ? (comp.played_with_record ?? 0) : (stats.played_with_record ?? 0);
  const _mc = isTotal ? (comp.matches_called ?? 0) : (stats.matches_called ?? 0);
  const minTotalPct = _pwr ? (isTotal ? comp.minutes_pct_total : stats.minutes_pct_total) : null;
  const minCalledPct = _mc ? (isTotal ? comp.minutes_pct_called : stats.minutes_pct_called) : null;
  const minScope = phase === "COMPETITION" ? "competition" : phase === "PRESEASON" ? "preseason" : "no_pre";

  // "Últimos partidos": solo partidos ya jugados por el equipo, más recientes primero.
  const lastMatches = (matches_detail || []).filter((m) => m.played);

  return (
    <div className="space-y-6" data-testid="player-dashboard">
      <div className="flex items-center justify-between">
        {backTeamId ? (
          <Button variant="ghost" size="sm"
            onClick={() => navigate(`/dashboard?team_id=${backTeamId}${backSeason ? `&season=${backSeason}` : ""}`)}
            data-testid="player-dashboard-back-team">
            <ArrowLeft className="h-4 w-4 mr-1" /> Volver al Dashboard del equipo
          </Button>
        ) : (
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="player-dashboard-back">
            <ArrowLeft className="h-4 w-4 mr-1" /> Volver
          </Button>
        )}
        <Button
          size="sm"
          className="bg-purple-700 hover:bg-purple-800"
          onClick={() => exportPlayerDashboardPdf(playerId, player.name, phase)}
          data-testid="player-dashboard-pdf-btn"
        >
          <FileDown className="h-4 w-4 mr-1" /> Descargar PDF
        </Button>
      </div>

      {/* Header — personal data */}
      <div className="rounded-2xl bg-gradient-to-r from-purple-800 to-purple-600 text-white p-6 flex flex-wrap items-center gap-6" data-testid="player-dashboard-header">
        {player.photo_id ? (
          <img
            src={`${process.env.REACT_APP_BACKEND_URL}/api/attachments/${player.photo_id}/file`}
            alt={player.name}
            className="w-20 h-20 rounded-2xl object-cover border border-white/30 shrink-0"
            data-testid="player-dashboard-photo"
          />
        ) : (
          <div className="w-20 h-20 rounded-2xl bg-white/15 border border-white/25 grid place-items-center text-3xl font-black shrink-0">
            {player.dorsal ?? "—"}
          </div>
        )}
        <div className="min-w-0 flex-1">
          <h1 className="text-2xl sm:text-3xl font-extrabold leading-tight" data-testid="player-dashboard-name">{player.name}</h1>
          <div className="flex flex-wrap gap-2 mt-2">
            {player.position && <Badge className="bg-white/20 text-white border-0">{player.position}</Badge>}
            {player.secondary_position && <Badge className="bg-white/10 text-purple-100 border-0">{player.secondary_position}</Badge>}
            <Badge className="bg-white/20 text-white border-0">{team.name}{team.category ? ` · ${team.category}` : ""}</Badge>
            {(() => {
              const ms = medicalStatus(player.medical_expiry);
              if (!ms) return null;
              const c = { rojo: "bg-rose-500", naranja: "bg-orange-500", amarillo: "bg-yellow-500 text-slate-900", ok: "bg-emerald-500" }[ms.level];
              return (
                <Badge className={`${c} text-white border-0`} data-testid="player-medical-badge">
                  R. médico: {ms.level === "rojo" ? `caducado (${ms.date})` : ms.level === "ok" ? ms.date : `caduca ${ms.date}`}
                </Badge>
              );
            })()}
          </div>
          <div className="flex flex-wrap gap-x-5 gap-y-1 mt-3 text-sm text-purple-100">
            {player.birth_year && <span className="inline-flex items-center gap-1.5"><CalendarDays className="h-4 w-4" /> {player.birth_year}</span>}
            {player.phone && <span className="inline-flex items-center gap-1.5"><Phone className="h-4 w-4" /> {player.phone}</span>}
            {player.phone_mother && <span className="inline-flex items-center gap-1.5"><Phone className="h-4 w-4" /> Madre: {player.phone_mother}</span>}
            {player.phone_father && <span className="inline-flex items-center gap-1.5"><Phone className="h-4 w-4" /> Padre: {player.phone_father}</span>}
          </div>
        </div>
      </div>

      {/* Selector de fase de temporada */}
      <div className="flex gap-1.5 flex-wrap items-center" data-testid="phase-selector">
        {[["", "Total temporada"], ["PRESEASON", "Pretemporada"], ["COMPETITION", "Competición"]].map(([k, l]) => (
          <button key={k} onClick={() => setPhase(k)}
            className={`px-3 py-1.5 rounded-full text-xs font-bold border transition-colors ${phase === k ? "bg-emerald-700 text-white border-emerald-700" : "bg-white text-slate-600 border-slate-200 hover:border-emerald-400"}`}
            data-testid={`phase-tab-${k || "total"}`}>
            {l}
          </button>
        ))}
        {loading && <Loader2 className="h-4 w-4 animate-spin text-emerald-600" />}
        {(stats.pending_events || 0) > 0 && phase === "" && (
          <span className="text-[10px] text-slate-400">({stats.pending_events} eventos con fase pendiente)</span>
        )}
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3">
        <Kpi icon={ClipboardCheck} label="% Asistencia" value={`${attPct}%`} sub={`${attSub}${gt.trainings_attended ? ` · +${gt.trainings_attended} invitado` : ""}`} color="text-emerald-600" testId="kpi-attendance" onClick={() => navigate(`/players/${playerId}/detalle-asistencia${detailQs}`)} />
        <Kpi icon={Users} label="Convocatorias" value={stats.matches_called + (gt.matches_called || 0)} sub={`${stats.titular} de titular${gt.matches_called ? ` · ${gt.matches_called} invitado` : ""}`} testId="kpi-called" onClick={() => navigate(`/players/${playerId}/detalle-partidos${detailQs}&metric=called`)} />
        <Kpi icon={Timer} label="Minutos" value={stats.minutes + (gt.minutes || 0)} sub={gt.minutes ? `+${gt.minutes}' invitado` : "jugados"} color="text-slate-700" testId="kpi-minutes" onClick={() => setMinBreakdown(true)} />
        <Kpi icon={Timer} label="% Min. equipo" value={minTotalPct == null ? "Sin datos" : `${String(minTotalPct).replace(".", ",")}%`} sub="sobre partidos del equipo" color={MIN_A.text} testId="kpi-min-total" onClick={() => setMinBreakdown(true)} />
        <Kpi icon={Timer} label="% Min. conv." value={minCalledPct == null ? "Sin datos" : `${String(minCalledPct).replace(".", ",")}%`} sub="en partidos convocados" color={MIN_B.text} testId="kpi-min-called" onClick={() => setMinBreakdown(true)} />
        <Kpi icon={Trophy} label="Goles" value={goals + (gt.goals || 0)} sub={gt.goals ? `+${gt.goals} como invitado` : undefined} color="text-amber-600" testId="kpi-goals" />
        <Kpi
          icon={Square}
          label="Tarjetas"
          value={(
            <span className="flex items-center gap-1.5">
              <span className="text-amber-500">{cards?.yellow ?? 0}</span>
              <span className="inline-block w-3 h-4 bg-amber-400 rounded-[3px]" />
              <span className="text-red-600 ml-1">{cards?.red ?? 0}</span>
              <span className="inline-block w-3 h-4 bg-red-500 rounded-[3px]" />
            </span>
          )}
          sub={`${cards?.yellow ?? 0} amarillas · ${cards?.red ?? 0} rojas`}
          color="text-amber-500"
          testId="kpi-cards"
        />
        <Kpi icon={Activity} label="Carga total" value={`${load.total_load} AU`} sub={`${load.sessions} sesiones · RPE ${load.avg_rpe}`} color="text-rose-600" testId="kpi-load" />
        <Kpi icon={HeartPulse} label="Wellness" value={wellness.entries ? `${wellness.avg_score}/20` : "—"} sub={wellness.entries ? `${wellness.greens}V · ${wellness.yellows}A · ${wellness.reds}R` : "sin registros"} color="text-pink-600" testId="kpi-wellness" />
      </div>

      {phase === "" && ((stats.pretemporada?.total_trainings || 0) + (stats.pretemporada?.minutes || 0) > 0) && (
        <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="season-split">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
            <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wide">Pretemporada vs Competición</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="rounded-lg bg-slate-50 p-3" data-testid="split-pretemporada">
              <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1.5">Pretemporada</div>
              <div className="flex flex-wrap gap-x-5 gap-y-1 text-sm">
                <span><b>{stats.pretemporada.attendance_pct}%</b> <span className="text-slate-500 text-xs">asistencia ({stats.pretemporada.asiste}/{stats.pretemporada.total_trainings})</span></span>
                <span><b>{stats.pretemporada.minutes}'</b> <span className="text-slate-500 text-xs">jugados</span></span>
                <span><b>{stats.pretemporada.matches_called}</b> <span className="text-slate-500 text-xs">convocatorias</span></span>
              </div>
            </div>
            <div className="rounded-lg bg-emerald-50 p-3" data-testid="split-competicion">
              <div className="text-[10px] font-black uppercase tracking-widest text-emerald-700 mb-1.5">Competición</div>
              <div className="flex flex-wrap gap-x-5 gap-y-1 text-sm">
                <span><b>{stats.competicion.attendance_pct}%</b> <span className="text-slate-500 text-xs">asistencia ({stats.competicion.asiste}/{stats.competicion.total_trainings})</span></span>
                <span><b>{stats.competicion.minutes}'</b> <span className="text-slate-500 text-xs">jugados</span></span>
                <span><b>{stats.competicion.matches_called}</b> <span className="text-slate-500 text-xs">convocatorias</span></span>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-4">
        <Section title="Asistencia a entrenamientos" icon={ClipboardCheck} testId="section-attendance">
          {attData.length ? (
            <ResponsiveContainer width="100%" height={230}>
              <PieChart>
                <Pie data={attData} dataKey="value" nameKey="name" innerRadius={55} outerRadius={85} paddingAngle={2}>
                  {attData.map((d) => <Cell key={d.name} fill={ATT_COLORS[d.name]} />)}
                </Pie>
                <Tooltip /><Legend />
              </PieChart>
            </ResponsiveContainer>
          ) : <Empty text="Sin registros de asistencia." />}
        </Section>

        <Section title="Participación en partidos" icon={Shirt} testId="section-matches-part">
          {stats.matches_called || stats.no_convocado ? (
            <ResponsiveContainer width="100%" height={230}>
              <BarChart data={convData}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} /><YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                <Tooltip />
                <Bar dataKey="v" name="Partidos" fill="#7E22CE" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : <Empty text="Sin convocatorias registradas." />}
        </Section>

        <Section title="Carga de entrenamiento (RPE)" icon={Activity} testId="section-load">
          <div className="mb-2"><span className={`text-xs font-bold px-2 py-1 rounded-full ${acwrBadge.cls}`} data-testid="acwr-badge">{acwrBadge.txt}</span></div>
          {rpeSeries.length ? (
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={rpeSeries}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="date" tick={{ fontSize: 10 }} /><YAxis tick={{ fontSize: 10 }} />
                <Tooltip />
                <Bar dataKey="carga" name="Carga (AU)" fill="#E11D48" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : <Empty text="Sin sesiones RPE registradas." />}
        </Section>

        <Section title="Evolución wellness" icon={HeartPulse} testId="section-wellness">
          {wellnessSeries.length ? (
            <ResponsiveContainer width="100%" height={228}>
              <LineChart data={wellnessSeries}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="date" tick={{ fontSize: 10 }} /><YAxis domain={[0, 20]} tick={{ fontSize: 10 }} />
                <Tooltip />
                <Line type="monotone" dataKey="score" name="Score /20" stroke="#DB2777" strokeWidth={2}
                  dot={({ cx, cy, payload, index }) => <circle key={index} cx={cx} cy={cy} r={3.5} fill={TL_COLOR[payload.tl] || "#DB2777"} />} />
              </LineChart>
            </ResponsiveContainer>
          ) : <Empty text="Sin registros de wellness." />}
        </Section>

        <Section title="Tests físicos (Sept · Enero · Mayo)" icon={Activity} testId="section-physical">
          {has_physical_data && physical_tests.length ? (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-left text-slate-500 border-b">
                    <th className="py-2 pr-2">Test</th><th className="py-2 px-2">Sept</th><th className="py-2 px-2">Enero</th><th className="py-2 px-2">Mayo</th>
                  </tr>
                </thead>
                <tbody>
                  {physical_tests.map((t) => (
                    <tr key={t.id} className="border-b last:border-0">
                      <td className="py-2 pr-2 font-semibold text-slate-700">{t.label}</td>
                      {["1", "2", "3"].map((e) => {
                        const c = t.values[e] || {};
                        return (
                          <td key={e} className="py-2 px-2">
                            {c.value != null ? (
                              <span className="inline-flex items-center gap-1.5">
                                <b>{c.value}</b>{t.unit !== "1-5" && <span className="text-slate-400">{t.unit}</span>}
                                <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${GRADE_STYLE[c.grade] || GRADE_STYLE["—"]}`}>{c.grade}</span>
                              </span>
                            ) : <span className="text-slate-300">—</span>}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : <Empty text="Sin tests físicos registrados." />}
        </Section>
      </div>

      <Section title="Evaluación técnica (Octubre vs Mayo)" icon={ClipboardCheck} testId="section-evaluation">
        {hasRadar ? (
          <div className="grid lg:grid-cols-2 gap-6 items-center">
            <div>
              <ResponsiveContainer width="100%" height={360}>
                <RadarChart data={radarData} outerRadius="72%">
                  <PolarGrid stroke="#CBD5E1" />
                  <PolarAngleAxis dataKey="short" tick={{ fontSize: 12, fontWeight: 600, fill: "#334155" }} />
                  <PolarRadiusAxis domain={[0, 4]} tickCount={5} tick={{ fontSize: 10, fill: "#94A3B8" }} angle={90} />
                  {hasOct && <Radar name="Octubre" dataKey="Octubre" stroke="#7E22CE" strokeWidth={2} fill="#7E22CE" fillOpacity={0.3} />}
                  {hasMay && <Radar name="Mayo" dataKey="Mayo" stroke="#F59E0B" strokeWidth={2} fill="#F59E0B" fillOpacity={0.28} />}
                  <Tooltip formatter={(v) => `${v}/4`} />
                  <Legend wrapperStyle={{ fontSize: 13, fontWeight: 600 }} />
                </RadarChart>
              </ResponsiveContainer>
              {radarIsSections && (
                <p className="text-xs text-slate-400 text-center -mt-1" data-testid="radar-source-note">
                  Media por sección del informe (sobre 4).
                </p>
              )}
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="eval-compare-table">
                <thead>
                  <tr className="text-left text-slate-500 border-b border-slate-200">
                    <th className="py-2 pr-2 font-semibold">Área</th>
                    <th className="py-2 px-2 text-center font-semibold text-purple-700">Oct</th>
                    <th className="py-2 px-2 text-center font-semibold text-amber-600">May</th>
                    <th className="py-2 pl-2 text-center font-semibold">Evol.</th>
                  </tr>
                </thead>
                <tbody>
                  {radarData.map((d) => {
                    const diff = hasOct && hasMay ? Math.round((d.Mayo - d.Octubre) * 100) / 100 : null;
                    const diffCls = diff == null ? "text-slate-300" : diff > 0 ? "text-emerald-600" : diff < 0 ? "text-red-500" : "text-slate-400";
                    const arrow = diff == null ? "—" : diff > 0 ? `▲ ${diff}` : diff < 0 ? `▼ ${Math.abs(diff)}` : "=";
                    return (
                      <tr key={d.area} className="border-b border-slate-100 last:border-0" data-testid={`eval-row-${d.area}`}>
                        <td className="py-2 pr-2 font-semibold text-slate-700">{d.area}</td>
                        <td className="py-2 px-2 text-center font-bold text-purple-700">{d.Octubre || "—"}</td>
                        <td className="py-2 px-2 text-center font-bold text-amber-600">{d.Mayo || "—"}</td>
                        <td className={`py-2 pl-2 text-center font-bold ${diffCls}`}>{arrow}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        ) : <Empty text="Sin evaluaciones registradas." />}
      </Section>

      <Section title="Informes individuales registrados" icon={ClipboardCheck} testId="section-informes">
        {(evaluations || []).length ? (
          <div className="space-y-3" data-testid="informes-list">
            {[...evaluations]
              .sort((a, b) => String(b.created_at || "").localeCompare(String(a.created_at || "")))
              .map((ev) => {
                const ratings = (global_rating_areas || [])
                  .map((a) => ({ label: a.label, val: ev.global_ratings?.[a.key] }))
                  .filter((r) => r.val != null && r.val !== "");
                return (
                  <div key={ev.id} className="rounded-xl border border-slate-200 p-4 flex flex-wrap items-center gap-3" data-testid={`informe-row-${ev.id}`}>
                    <div className="flex items-center gap-2">
                      <Badge className="bg-purple-600 text-white border-0" data-testid={`informe-period-${ev.id}`}>
                        {EVAL_PERIOD_LABEL[ev.period] || ev.period}
                      </Badge>
                      <span className="text-xs font-bold text-slate-600">{ev.season}</span>
                    </div>
                    {ev.created_at && (
                      <span className="text-xs text-slate-400 flex items-center gap-1">
                        <CalendarDays className="h-3.5 w-3.5" />
                        {new Date(ev.created_at).toLocaleDateString("es-ES", { day: "2-digit", month: "short", year: "numeric" })}
                      </span>
                    )}
                    <div className="flex flex-wrap gap-1.5">
                      {ratings.length ? ratings.map((r, i) => (
                        <span key={i} className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-slate-100 text-slate-700">
                          {r.label} <b className="text-purple-700">{r.val}</b>
                        </span>
                      )) : <span className="text-xs text-slate-400">Sin valoración global</span>}
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      className="ml-auto border-purple-300 text-purple-700 hover:bg-purple-50 font-bold"
                      onClick={() => exportPlayerEvaluationPdf(playerId, player.name, ev.season, ev.period)}
                      data-testid={`informe-pdf-${ev.id}`}
                    >
                      <FileDown className="h-4 w-4 mr-1.5" /> PDF
                    </Button>
                  </div>
                );
              })}
          </div>
        ) : <Empty text="Aún no hay informes individuales registrados para este jugador." />}
      </Section>

      <Section title="Últimos partidos" icon={Shirt} testId="section-last-matches">
        {lastMatches.length ? (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-left text-slate-500 border-b">
                  <th className="py-2 pr-2">Fecha</th><th className="py-2 px-2">Rival</th><th className="py-2 px-2">Local/Fuera</th>
                  <th className="py-2 px-2">Resultado</th><th className="py-2 px-2">Estado</th><th className="py-2 px-2 text-right">Min</th>
                </tr>
              </thead>
              <tbody>
                {lastMatches.map((m, i) => (
                  <tr key={i} className="border-b last:border-0">
                    <td className="py-2 pr-2">{m.date}</td>
                    <td className="py-2 px-2 font-semibold text-slate-700">{m.rival || "—"}</td>
                    <td className="py-2 px-2">{m.home_away === "casa" ? "En casa" : "Fuera"}</td>
                    <td className="py-2 px-2">{m.result || "—"}</td>
                    <td className="py-2 px-2">{STATUS_LABEL[m.status] || "—"}</td>
                    <td className="py-2 px-2 text-right font-bold">{m.minutes ?? 0}'</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : <Empty text="El equipo aún no ha jugado ningún partido." />}
      </Section>

      {hasGuest && (
        <Section title="Participación como invitado (otros equipos del club)" icon={Users} testId="section-guest-participation">
          <div className="flex flex-wrap gap-2 mb-4">
            <span className="px-2.5 py-1 rounded-full bg-amber-100 text-amber-800 text-xs font-bold" data-testid="guest-total-matches">{gt.matches_called || 0} partidos</span>
            <span className="px-2.5 py-1 rounded-full bg-amber-100 text-amber-800 text-xs font-bold" data-testid="guest-total-minutes">{gt.minutes || 0}' jugados</span>
            <span className="px-2.5 py-1 rounded-full bg-amber-100 text-amber-800 text-xs font-bold" data-testid="guest-total-goals">{gt.goals || 0} goles</span>
            <span className="px-2.5 py-1 rounded-full bg-amber-100 text-amber-800 text-xs font-bold" data-testid="guest-total-trainings">{gt.trainings_attended || 0} entrenos</span>
          </div>
          {guest.matches?.length > 0 && (
            <div className="overflow-x-auto mb-4" data-testid="guest-matches-table">
              <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wide mb-1">Partidos como invitado</p>
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-left text-slate-500 border-b">
                    <th className="py-2 pr-2">Fecha</th><th className="py-2 px-2">Equipo</th><th className="py-2 px-2">Rival</th>
                    <th className="py-2 px-2">Resultado</th><th className="py-2 px-2">Estado</th><th className="py-2 px-2 text-right">Min</th>
                  </tr>
                </thead>
                <tbody>
                  {guest.matches.map((m, i) => (
                    <tr key={i} className="border-b last:border-0">
                      <td className="py-2 pr-2">{m.date}</td>
                      <td className="py-2 px-2"><span className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 font-bold">{m.team_name || "—"}</span></td>
                      <td className="py-2 px-2 font-semibold text-slate-700">{m.rival || "—"}</td>
                      <td className="py-2 px-2">{m.result || "—"}</td>
                      <td className="py-2 px-2">{STATUS_LABEL[m.status] || m.status}</td>
                      <td className="py-2 px-2 text-right font-bold">{m.minutes ?? 0}'{m.goals ? ` · ${m.goals}⚽` : ""}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {guest.trainings?.length > 0 && (
            <div className="overflow-x-auto" data-testid="guest-trainings-table">
              <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wide mb-1">Entrenamientos como invitado</p>
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-left text-slate-500 border-b">
                    <th className="py-2 pr-2">Fecha</th><th className="py-2 px-2">Equipo</th><th className="py-2 px-2">Asistencia</th>
                  </tr>
                </thead>
                <tbody>
                  {guest.trainings.map((t, i) => (
                    <tr key={i} className="border-b last:border-0">
                      <td className="py-2 pr-2">{t.date}</td>
                      <td className="py-2 px-2"><span className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 font-bold">{t.team_name || "—"}</span></td>
                      <td className="py-2 px-2">{{ asiste: "Asiste", retraso: "Retraso", no_asiste: "No asiste", enfermo: "Enfermo", lesion: "Lesión", motivos_personales: "Personal" }[t.status] || t.status}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Section>
      )}

      <Section title="Datos RFFM (histórico oficial)" icon={Shirt} testId="section-player-rffm">
        <PlayerRffmSection playerId={player.id} />
      </Section>

      <Section title="Imágenes, documentos y vídeos" icon={Paperclip} testId="section-player-docs">
        <AttachmentsPanel
          scope="player_doc"
          scopeId={player.id}
          canEdit={user?.role === "admin" || (user?.team_ids || []).includes(player.team_id)}
          allowLinks
          maxFiles={10}
          title="Archivos del jugador"
          hint="JPG, PNG, PDF, DOC, DOCX, PPT, PPTX · máx. 10 MB · o añade un enlace de vídeo"
          emptyLabel="Aún no hay archivos ni vídeos de este jugador."
        />
      </Section>

      <MinutesBreakdownDialog
        open={minBreakdown}
        onClose={() => setMinBreakdown(false)}
        playerId={playerId}
        playerName={player.name}
        scope={minScope}
        dateFrom={backSeason ? `${backSeason}-07-01` : undefined}
        dateTo={backSeason ? `${Number(backSeason) + 1}-06-30` : undefined}
      />
    </div>
  );
}
